import React from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  PageHeader,
  RowNumberCell,
} from "../../../components/ui";

import Checkbox from "@mui/material/Checkbox/Checkbox";
import Box from "@mui/material/Box/Box";

import {
  AttorneyLeadData,
  CategoryMapping,
  LeadType,
  Stage,
  StageColorsMapping,
} from "../../Admin/LeadsManagement/shared/Constants";
import useNavigateSearch from "../../../services/useNavigateSearch";
import { Category } from "../../../api";

const TABLE_COLUMNS = [
  { id: "status", label: "Stage", sortable: false },
  { id: "category", label: "Category", sortable: false },
  { id: "employer", label: "Employer", sortable: false },
  { id: "updatedAt", label: "Last update", sortable: false },
];

export function createData(
  id: string,
  stage: Stage,
  category: Category,
  employer: string,
  lastUpdate: string
): AttorneyLeadData {
  return {
    id,
    stage,
    category,
    employer,
    lastUpdate,
  };
}

const DEFAULT_ROWS_PER_PAGE = 5;

export default function CasesTable() {
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [page, setPage] = React.useState(0);
  const [visibleRows, setVisibleRows] = React.useState<AttorneyLeadData[] | null>(null);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const navigate = useNavigateSearch();

  // TODO: get rows from BE
  React.useEffect(() => {
    let rowsOnMount = filteredRows;
    rowsOnMount = rowsOnMount.slice(
      0 * DEFAULT_ROWS_PER_PAGE,
      0 * DEFAULT_ROWS_PER_PAGE + DEFAULT_ROWS_PER_PAGE
    );

    setVisibleRows(rowsOnMount);
  }, []);

  const handleClick = (row: AttorneyLeadData) => {
    const selectedIndex = selected.indexOf(row.id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = selected.concat(row.id);
    } else if (selectedIndex === 0) {
      newSelected = selected.slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = selected.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = selected
        .slice(0, selectedIndex)
        .concat(selected.slice(selectedIndex + 1));
    }

    setSelected(newSelected);
  };

  const handleChangePage = React.useCallback(
    (newPage: number) => {
      setPage(newPage);

      const sortedRows = filteredRows.slice();
      const updatedRows = sortedRows.slice(
        newPage * rowsPerPage,
        newPage * rowsPerPage + rowsPerPage
      );
      setVisibleRows(updatedRows);
    },
    [rowsPerPage]
  );

  const handleChangeRowsPerPage = React.useCallback((updatedRowsPerPage: number) => {
    setRowsPerPage(updatedRowsPerPage);
    setPage(0);

    const sortedRows = filteredRows.slice();
    const updatedRows = sortedRows.slice(
      0 * updatedRowsPerPage,
      0 * updatedRowsPerPage + updatedRowsPerPage
    );
    setVisibleRows(updatedRows);
  }, []);

  const handleChangeFilter = (stage: Stage | null = null) => {
    if (!!selected.length) {
      setSelected([]);
    }
    setPage(0);

    let newRows = rows;
    if (!!stage) {
      newRows = rows.slice().filter((row) => row.stage === stage);
    }

    filteredRows = newRows;
    const updatedRows = newRows.slice(0, rowsPerPage);
    setVisibleRows(updatedRows);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  // TODO: update if needed
  const handleDetailsClick = (e: React.MouseEvent<unknown> | null, row) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    navigate(`/lead/${row.id}`, { leadType: LeadType.Case, isEdit: "false" });
  };

  const rows = [
    createData("1", Stage.new, Category.WORKERS_COMPENSATION, "Apple", "19.03.2023"),
    createData("2", Stage.new, Category.WORKERS_COMPENSATION, "Apple co", "19.03.2023"),
  ];
  let filteredRows = rows;

  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        title="Cases"
        subtitle="Matters and cases assigned to you."
      />
      <div className="row mb-5">
        <div className="col-12 d-flex table-filters">
          <AppButton
            variant="contained"
            color="darkGray"
            size="small"
            onClick={() => handleChangeFilter()}
          >
            All
          </AppButton>
          <AppButton
            variant="contained"
            color="cyan"
            size="small"
            onClick={() => handleChangeFilter(Stage.new)}
          >
            New
          </AppButton>
        </div>
      </div>
      <AppTable
        columns={TABLE_COLUMNS}
        showRowNumber
        size="medium"
        leadingHeadCell={<AppTableHeadCell padding="checkbox" />}
        trailingHeadCells={
          <>
            <AppTableHeadCell />
            <AppTableHeadCell>Actions</AppTableHeadCell>
          </>
        }
        extraColumnCount={3}
        isEmpty={!visibleRows?.length}
        emptyTitle="No cases"
        pagination={{
          count: filteredRows.length,
          page,
          rowsPerPage,
          rowsPerPageOptions: [5, 10, 25, 50, 100],
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
        }}
      >
        {visibleRows
          ? visibleRows.map((row, index) => {
              const isItemSelected = isSelected(row.id);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <AppTableRow
                  onClick={() => handleClick(row)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id}
                  selected={isItemSelected}
                  sx={{ cursor: "pointer" }}
                >
                  <AppTableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      inputProps={{
                        "aria-labelledby": labelId,
                      }}
                    />
                  </AppTableCell>
                  <RowNumberCell value={page * rowsPerPage + index + 1} />
                  <AppTableCell align="left">
                    <AppButton
                      variant="contained"
                      color={StageColorsMapping[row.stage]}
                      size="small"
                    >
                      {row.stage}
                    </AppButton>
                  </AppTableCell>
                  <AppTableCell align="left">
                    {CategoryMapping[row.category]}
                  </AppTableCell>
                  <AppTableCell align="left">{row.employer}</AppTableCell>
                  <AppTableCell align="left">{row.lastUpdate}</AppTableCell>
                  <AppTableCell />
                  <AppTableCell align="left">
                    <AppButton
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={(e) => handleDetailsClick(e, row)}
                    >
                      View details
                    </AppButton>
                  </AppTableCell>
                </AppTableRow>
              );
            })
          : null}
      </AppTable>
    </Box>
  );
}
